import React, { useState } from "react";
import axios from "axios";
import { MapPin, Mail, Instagram, Youtube, Facebook, Phone, CheckCircle, Send } from "lucide-react";
import PageHero from "@/components/PageHero";
import FloatingEditButton from "@/components/FloatingEditButton";
import SEO from "@/components/SEO";
import TikTok from "@/components/icons/TikTok";
import Reveal from "@/components/motion/Reveal";
import Section from "@/components/ui/Section";
import CTAButton from "@/components/ui/CTAButton";
import Field, { FormError } from "@/components/ui/Field";
import { API } from "@/lib/api";

const DIRECCION = "Avd. Manuel Siurot, 31 · 21004 Huelva";
const EMAIL = "evangelizaciondigital@diocesisdehuelva.es";

const REDES = [
  { href: "https://instagram.com/evangelizaciondigitalhuelva", icon: Instagram, label: "Instagram", handle: "@evangelizaciondigitalhuelva" },
  { href: "https://facebook.com/profile.php?id=61579343082913", icon: Facebook, label: "Facebook", handle: "Evangelización Digital Huelva" },
  { href: "https://youtube.com/@EvangelizacionDigitalHuelva", icon: Youtube, label: "YouTube", handle: "@EvangelizacionDigitalHuelva" },
  { href: "https://tiktok.com/@evangelizacion.di", icon: TikTok, label: "TikTok", handle: "@evangelizacion.di" },
];

export default function Contacto() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", honeypot: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError("Por favor, completa los campos obligatorios.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form, { withCredentials: true });
      setSuccess(true);
    } catch {
      setError(`Ha ocurrido un problema al enviar tu mensaje. Por favor, escríbenos directamente a ${EMAIL}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contacto-page">
      <SEO
        title="Contacto"
        description="Escríbenos para cualquier consulta, propuesta de colaboración o solicitud de formación en evangelización digital."
      />
      <PageHero slug="contacto" />

      <Section tone="light">
        <div className="grid md:grid-cols-2 gap-16">
          {/* Información de contacto */}
          <Reveal distance={24}>
            <h2 className="font-heading text-2xl font-bold mb-8">Información de Contacto</h2>
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 shadow-sm">
                  <MapPin className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} aria-hidden="true" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-sm mb-0.5">Dirección</p>
                  <p className="text-sm text-muted-foreground">
                    Delegación de Evangelización Digital<br />Diócesis de Huelva<br />{DIRECCION}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Phone className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} aria-hidden="true" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-sm mb-0.5">Teléfono</p>
                  <a href="tel:+34959252100" className="text-sm text-primary hover:underline focus-ring rounded">
                    959 25 21 00
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Mail className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-sm mb-0.5">Email</p>
                  <a href={`mailto:${EMAIL}`} className="text-sm text-primary hover:underline break-words focus-ring rounded">
                    {EMAIL}
                  </a>
                </div>
              </div>
            </div>

            <h3 className="font-heading font-semibold text-base mb-4">Redes Sociales</h3>
            <div className="space-y-3">
              {REDES.map(s => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`contact-social-${s.label.toLowerCase()}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors duration-fast focus-ring"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                    <s.icon className="w-4 h-4 text-primary-foreground" size={16} aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{s.label}</p>
                    <p className="text-xs text-muted-foreground truncate">{s.handle}</p>
                  </div>
                </a>
              ))}
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden border border-border h-48 bg-muted flex items-center justify-center">
              <div className="text-center px-6">
                <MapPin className="w-8 h-8 text-muted-foreground mx-auto mb-2" aria-hidden="true" />
                <p className="text-sm text-muted-foreground">Diócesis de Huelva<br />{DIRECCION}</p>
              </div>
            </div>
          </Reveal>

          {/* Formulario */}
          <Reveal delay={80} distance={24}>
            <h2 className="font-heading text-2xl font-bold mb-8">Envíanos un Mensaje</h2>
            {success ? (
              <div data-testid="contact-success" className="text-center py-16" role="status">
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-primary-foreground" aria-hidden="true" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-3">¡Mensaje enviado!</h3>
                <p className="text-muted-foreground">
                  Tu mensaje ha sido enviado correctamente. Nos pondremos en contacto contigo pronto.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5" noValidate>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="Nombre"
                    required
                    data-testid="contact-input-name"
                    type="text"
                    name="name"
                    autoComplete="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                  />
                  <Field
                    label="Email"
                    required
                    data-testid="contact-input-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="Teléfono"
                    data-testid="contact-input-phone"
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="600 000 000"
                  />
                  <Field
                    label="Asunto"
                    data-testid="contact-input-subject"
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    placeholder="¿En qué podemos ayudarte?"
                  />
                </div>

                {/* Señuelo anti-spam: oculto también para tecnología asistiva. */}
                <input
                  type="text"
                  name="honeypot"
                  value={form.honeypot}
                  onChange={handleChange}
                  style={{ display: "none" }}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />

                <Field
                  label="Mensaje"
                  required
                  as="textarea"
                  data-testid="contact-input-message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Escríbenos tu consulta, propuesta o petición..."
                />

                <FormError data-testid="contact-error">{error}</FormError>

                <CTAButton
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={loading}
                  className="w-full py-3.5 font-bold"
                >
                  {loading ? (
                    "Enviando..."
                  ) : (
                    <>
                      <Send className="w-4 h-4" aria-hidden="true" />
                      <span>Enviar Mensaje</span>
                    </>
                  )}
                </CTAButton>
              </form>
            )}
          </Reveal>
        </div>
      </Section>

      <FloatingEditButton slug="contacto" />
    </div>
  );
}
